import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAppVersion } from "@/hooks/use-app-version";

/**
 * VIB-2834: «Revehiet er oppdatert». Vises når en nyere versjon er deployet
 * enn den fanen kjører, og ber brukeren laste inn på nytt. Har versjonen en
 * utrulling brukeren får se, vises tittelen på den med en gang. Ellers vises
 * varselet høyst én gang per døgn (se `useAppVersion`).
 *
 * Laster aldri inn av seg selv: brukeren kan sitte midt i et tilbud eller en
 * timeføring. «Senere» skjuler varselet for akkurat denne versjonen; kommer
 * det en ny deploy etterpå, vises det igjen etter de samme reglene.
 *
 * Plassert nederst i midten, unna chat-knappen til høyre og timerlinja på
 * mobil, og som `status` så skjermlesere leser det uten å flytte fokus.
 */
export default function UpdateAvailableBanner() {
  const { updateAvailable, deployedVersion, release } = useAppVersion();
  const [skjultFor, setSkjultFor] = useState<string | null>(null);

  if (!updateAvailable || skjultFor === deployedVersion) return null;

  return (
    <div
      role="status"
      className="fixed bottom-24 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-background p-4 shadow-lg md:bottom-6"
    >
      <SrIcon name="refresh-cw" className="h-5 w-5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {release ? `Nytt i Revehiet: ${release.title}` : "Revehiet er oppdatert"}
        </p>
        <p className="text-sm text-muted-foreground">
          {release ? "Last inn på nytt for å ta det i bruk." : "Last inn på nytt for å få siste versjon."}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="ghost" size="sm" onClick={() => setSkjultFor(deployedVersion)}>
          Senere
        </Button>
        <Button size="sm" onClick={() => window.location.reload()}>
          Last inn på nytt
        </Button>
      </div>
    </div>
  );
}
